/**
 * Animated background: colour keyframes over time.
 *
 * Every mode animates the same way — the background walks through a list of
 * colour states — but what one state means differs. For Solid a state is a
 * single colour, for the ramps it is the whole set of stops, for Mesh it is the
 * five point colours. Keeping them all as "a list of colours" lets one model,
 * one editor and one interpolator serve all four instead of four of each.
 *
 * Mesh additionally animates its arrangement rather than its colour. Every
 * authored colour point travels around a deterministic circular orbit whose
 * centre is chosen by a motion seed. That is a separate switch because a
 * moving mesh with fixed colours is a common thing to want, and so is the
 * reverse.
 */

import type { MeshPoint } from './meshGradientPoints';

export type BackgroundAnimationKeyframe = string[];

export interface BackgroundAnimationState {
  enabled: boolean;
  loop: boolean;
  /** Seconds of finished video. */
  duration: number;
  /** 0..100; how quickly colour keyframes are traversed. */
  speed: number;
  /** Mesh only: move the authored points along seamless circular orbits. */
  meshMove: boolean;
  /** Mesh only: cross-fade the point colours. */
  meshColor: boolean;
  /** Mesh only: 0..100 linear travel speed of the colour points. */
  meshMotionSpeed: number;
  /** Mesh only: deterministically chooses each orbit centre and direction. */
  meshMotionSeed: number;
  keyframes: BackgroundAnimationKeyframe[];
}

export const BACKGROUND_ANIMATION_DURATIONS = [5, 10, 15, 30];
export const BACKGROUND_ANIMATION_MAX_KEYFRAMES = 5;

export function createBackgroundAnimationState(): BackgroundAnimationState {
  return {
    enabled: false,
    loop: true,
    duration: 10,
    speed: 30,
    meshMove: true,
    meshColor: false,
    meshMotionSpeed: 30,
    meshMotionSeed: 37,
    keyframes: []
  };
}

/**
 * How far through the keyframe list we are at a given moment.
 *
 * Speed is expressed as cycles over the whole clip rather than as an abstract
 * rate, so the same setting means the same thing whether the export is five
 * seconds or thirty — a background tuned at 10s does not have to be re-tuned
 * after switching to 30s.
 */
export function getAnimationProgress(
  animation: BackgroundAnimationState,
  timeSeconds: number
): number {
  const duration = Math.max(0.001, animation.duration);
  const requested = 0.25 + (clamp(animation.speed, 0, 100) / 100) * 3.75;
  // A loop that does not meet itself is not a loop: the clip has to hold a
  // whole number of cycles, so the speed is rounded to the nearest one that
  // divides the duration evenly. Without looping the speed is taken literally
  // and the walk simply ends wherever it ends.
  const cycles = animation.loop ? Math.max(1, Math.round(requested)) : requested;
  const raw = (timeSeconds / duration) * cycles;
  if (!animation.loop) {
    return clamp(raw, 0, 1);
  }
  return raw - Math.floor(raw);
}

/**
 * The colours to draw at a given progress.
 *
 * With looping on, the last keyframe blends back into the first so the clip can
 * be cut anywhere without a seam. With it off the walk simply ends on the last
 * one.
 */
export function sampleKeyframes(
  keyframes: BackgroundAnimationKeyframe[],
  progress: number,
  loop: boolean
): string[] | null {
  const usable = keyframes.filter((frame) => frame.length > 0);
  if (usable.length === 0) {
    return null;
  }
  if (usable.length === 1) {
    return [...usable[0]];
  }

  const segments = loop ? usable.length : usable.length - 1;
  const position = clamp(progress, 0, 1) * segments;
  const index = Math.min(Math.floor(position), segments - 1);
  const t = position - index;
  const from = usable[index % usable.length];
  const to = usable[(index + 1) % usable.length];
  const width = Math.max(from.length, to.length);

  const mixed: string[] = [];
  for (let slot = 0; slot < width; slot += 1) {
    mixed.push(mixHex(from[slot] ?? from[from.length - 1], to[slot] ?? to[to.length - 1], t));
  }
  return mixed;
}

/** Maximum point travel per second at Speed 100, in canvas-space units. */
const MESH_ORBIT_MAX_LINEAR_SPEED = 0.24;
/** Caps extreme duration/speed combinations while retaining the full 3x range. */
const MESH_ORBIT_MAX_RADIUS = 0.96;

/**
 * Moves every authored Mesh point around its own circular orbit.
 *
 * The authored point is on the circumference, so t=0 always reproduces the
 * exact static composition. The orbit radius is derived from linear speed and
 * clip duration (`r = vT / 2π`): five seconds produces a restrained circle,
 * while a longer clip has room for a larger movement without making the point
 * travel faster. Every point shares the same radius and angular phase, so their
 * absolute speed is identical. The seed only chooses where each circle centre
 * sits relative to its authored point and whether it turns clockwise.
 */
export function sampleMeshOrbitPoints(
  points: MeshPoint[],
  animation: BackgroundAnimationState,
  timeSeconds: number
): MeshPoint[] {
  const duration = Math.max(0.001, animation.duration);
  const progress = clamp(timeSeconds / duration, 0, 1);
  const motionSpeed = Number.isFinite(animation.meshMotionSpeed)
    ? animation.meshMotionSpeed
    : animation.speed;
  const linearSpeed = (clamp(motionSpeed, 0, 100) / 100) * MESH_ORBIT_MAX_LINEAR_SPEED;
  const radius = Math.min(MESH_ORBIT_MAX_RADIUS, (linearSpeed * duration) / (Math.PI * 2));

  if (radius <= 0 || points.length === 0) {
    return points.map((point) => ({ ...point }));
  }

  // Loop makes one exact revolution. A non-looping animation follows an open
  // three-quarter arc and holds there, so it never simulates a reverse pass.
  const phase = progress * Math.PI * 2 * (animation.loop ? 1 : 0.75);
  const seed = Number.isFinite(animation.meshMotionSeed) ? animation.meshMotionSeed : 0;

  return points.map((point, index) => {
    const centreAngle = hashUnit(seed, index, 0) * Math.PI * 2;
    const direction = hashUnit(seed, index, 1) < 0.5 ? -1 : 1;
    const radialX = Math.cos(centreAngle);
    const radialY = Math.sin(centreAngle);
    const centreX = point.x - radialX * radius;
    const centreY = point.y - radialY * radius;
    const angle = centreAngle + direction * phase;

    return {
      x: centreX + Math.cos(angle) * radius,
      y: centreY + Math.sin(angle) * radius
    };
  });
}

/** Stable pseudo-random 0..1 value: preview and export must pick the same orbit. */
function hashUnit(seed: number, pointIndex: number, salt: number): number {
  let value =
    (Math.round(seed) + 1) ^
    Math.imul(pointIndex + 1, 0x45d9f3b) ^
    Math.imul(salt + 1, 0x27d4eb2d);
  value = Math.imul(value ^ (value >>> 16), 0x7feb352d);
  value = Math.imul(value ^ (value >>> 15), 0x846ca68b);
  return ((value ^ (value >>> 16)) >>> 0) / 4294967296;
}

/** A new keyframe copies the one before it, so the author edits a difference
 *  rather than building a state from nothing. */
export function appendKeyframe(
  keyframes: BackgroundAnimationKeyframe[],
  fallback: string[]
): BackgroundAnimationKeyframe[] {
  if (keyframes.length >= BACKGROUND_ANIMATION_MAX_KEYFRAMES) {
    return keyframes;
  }
  const source = keyframes.length > 0 ? keyframes[keyframes.length - 1] : fallback;
  return [...keyframes, [...source]];
}

export function removeKeyframe(
  keyframes: BackgroundAnimationKeyframe[],
  index: number
): BackgroundAnimationKeyframe[] {
  if (keyframes.length <= 1 || index < 0 || index >= keyframes.length) {
    return keyframes;
  }
  return keyframes.filter((_, position) => position !== index);
}

/** Cross-fades two `#rrggbb` values. Straight sRGB, matching how the gradients
 *  themselves blend, so an animated ramp passes through the same colours a
 *  static one would. */
export function mixHex(from: string, to: string, t: number): string {
  const a = parseHex(from);
  const b = parseHex(to);
  if (!a || !b) {
    return t < 0.5 ? from : to;
  }
  const amount = clamp(t, 0, 1);
  const channel = (index: number) => Math.round(a[index] + (b[index] - a[index]) * amount);
  return `#${[0, 1, 2].map((index) => channel(index).toString(16).padStart(2, '0')).join('')}`;
}

function parseHex(value: string): [number, number, number] | null {
  const match = /^#?([0-9a-f]{6})$/i.exec((value || '').trim());
  if (!match) {
    return null;
  }
  const int = parseInt(match[1], 16);
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
